import React from 'react';

// Task 28 — Per-turn telemetry footer (spec § 3.3).
//
// Compact one-liner below each assistant message. Reads from
// `message.metadata.turn_telemetry` (Wave 6 Task 24 shape).
//
// Degradation contract:
//   - estimated_cost_usd = null → render `~$—`
//   - total_tokens = null → render `— tokens`
//   - cached_input_tokens = 0 → omit the `(cached ↓)` segment
//   - tool_call_count = 0 → drop the tool count entirely
//   - never emit NaN / undefined / empty footer

export interface TurnTelemetryShape {
  tool_call_count?: number;
  total_duration_ms?: number;
  total_tokens?: number | null;
  cached_input_tokens?: number | null;
  input_tokens?: number | null;
  estimated_cost_usd?: number | null;
  turn_status?: string;
  turn_status_reason?: string | null;
}

interface Props {
  telemetry: TurnTelemetryShape;
  darkMode?: boolean;
  // When provided, the tool-count segment becomes a clickable chevron
  // toggle — operator clicks to reveal the per-call pill grid inline
  // above the footer. Parent owns the open state and the pill render.
  toolCallsOpen?: boolean;
  onToggleToolCalls?: () => void;
  // Authoritative tool count when the parent has it (derived from
  // tool_calls.length). Falls back to telemetry.tool_call_count when
  // omitted — protects against a turn_telemetry where the persisted
  // count disagrees with the tool_calls list.
  toolCountOverride?: number;
}

function formatDuration(ms: number | undefined | null): string {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function formatTokens(tokens: number | undefined | null): string {
  if (tokens == null) return '— tokens';
  return `${tokens.toLocaleString()} tokens`;
}

function formatCost(usd: number | undefined | null): string {
  if (usd == null) return '~$—';
  if (usd === 0) return '~$0.00';
  if (usd < 0.001) return `~$${usd.toFixed(5)}`;
  if (usd < 0.01) return `~$${usd.toFixed(4)}`;
  return `~$${usd.toFixed(3)}`;
}

// Rough dollars saved by cache: assumes Anthropic 90% discount on
// cache reads + 25% premium on cache writes nets out favorably. We
// don't have per-call breakdown here, so approximate as cost of
// cached_input_tokens at the input rate — good enough for a tooltip
// message that's about teaching the pattern, not accounting.
function cacheSavingsHint(
  cached: number | undefined | null,
  totalInput: number | undefined | null,
  totalCost: number | undefined | null,
): string | null {
  if (!cached || cached <= 0) return null;
  if (totalCost == null || totalInput == null || totalInput <= 0) {
    return `${cached.toLocaleString()} input tokens served from cache`;
  }
  // savings ≈ (cached / total_input) * (input_portion_of_cost). We
  // don't split prompt vs completion here, so use a conservative 60%
  // as the input-cost share + 90% discount on cached portion.
  const savings = totalCost * 0.6 * (cached / totalInput) * 0.9;
  const savingsStr = savings < 0.001 ? savings.toFixed(5) : savings.toFixed(3);
  return `${cached.toLocaleString()} of ${totalInput.toLocaleString()} input tokens served from cache — saved ~$${savingsStr}`;
}

export const TurnTelemetryFooter: React.FC<Props> = ({
  telemetry,
  darkMode,
  toolCallsOpen,
  onToggleToolCalls,
  toolCountOverride,
}) => {
  if (!telemetry) return null;
  const toolCount = toolCountOverride ?? telemetry.tool_call_count ?? 0;
  const duration = telemetry.total_duration_ms ?? null;
  const tokens = telemetry.total_tokens ?? null;
  const cached = telemetry.cached_input_tokens ?? 0;
  const cost = telemetry.estimated_cost_usd ?? null;

  const parts: React.ReactNode[] = [];

  if (toolCount > 0) {
    const label = `${toolCount} ${toolCount === 1 ? 'tool' : 'tools'}`;
    if (onToggleToolCalls) {
      parts.push(
        <button
          key="tools"
          type="button"
          onClick={onToggleToolCalls}
          aria-expanded={!!toolCallsOpen}
          className="inline-flex items-center gap-0.5 opacity-80 hover:opacity-100 underline-offset-2 hover:underline"
        >
          <span className="opacity-60">{toolCallsOpen ? '▾' : '▸'}</span>
          {label}
        </button>
      );
    } else {
      parts.push(
        <span key="tools" className="opacity-80">
          {label}
        </span>
      );
    }
  }

  parts.push(
    <span key="dur" className="opacity-80">
      {formatDuration(duration)}
    </span>
  );

  parts.push(
    <span key="tok" className="opacity-80">
      {formatTokens(tokens)}
      {cached > 0 && (
        <span
          className="relative inline-flex items-center ml-1 group/cache cursor-help"
          aria-label="Cache hit details"
        >
          <span className="opacity-70">(</span>
          <span className="text-indigo-400 dark:text-indigo-300">{cached.toLocaleString()} cached ↓</span>
          <span className="opacity-70">)</span>
          <span
            className={`invisible group-hover/cache:visible absolute bottom-full left-1/2 -translate-x-1/2 mb-1 z-30 whitespace-nowrap rounded px-2 py-1 text-[10px] font-mono shadow-lg pointer-events-none ${
              darkMode
                ? 'bg-gray-900 border border-gray-600 text-gray-200'
                : 'bg-gray-800 border border-gray-700 text-gray-100'
            }`}
          >
            {cacheSavingsHint(cached, telemetry.input_tokens, cost)}
          </span>
        </span>
      )}
    </span>
  );

  parts.push(
    <span key="cost" className="opacity-80">
      {formatCost(cost)}
    </span>
  );

  if (telemetry.turn_status && telemetry.turn_status !== 'ok') {
    parts.push(
      <span key="status" className="text-amber-500 dark:text-amber-300 opacity-90">
        · {telemetry.turn_status}
        {telemetry.turn_status_reason ? ` (${telemetry.turn_status_reason})` : ''}
      </span>
    );
  }

  return (
    <div className="text-[11px] font-mono mt-1.5 pt-1.5 border-t border-gray-500/20 flex flex-wrap gap-x-2 gap-y-0.5 opacity-80">
      {parts.map((part, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span className="opacity-50">·</span>}
          {part}
        </React.Fragment>
      ))}
    </div>
  );
};

export default TurnTelemetryFooter;
